import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SkeletonGrid } from '../components/ui/Skeleton';
import ConfirmModal from '../components/ui/ConfirmModal';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../components/auth/AuthContext';
import { SCENARIOS } from '../data/scenarios';

const SCENARIO_TABS = [
  { key: '', label: '전체' },
  { key: 'math_study', label: '🔢 수학 오답' },
  { key: 'study_sheet', label: '📝 학습 시트' },
  { key: 'hvac_inspection', label: '❄️ HVAC' },
  { key: 'fire_safety', label: '🧯 소방' },
  { key: 'exercise_coaching', label: '🏋️ 운동' },
  { key: 'health_checkup', label: '🏥 건강' },
  { key: '_legacy', label: '📚 기존 오답' },
];

export default function RecordList() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScenario, setSelectedScenario] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Confirm modal state
  const [deleteTarget, setDeleteTarget] = useState(null);

  const user_id = user.user_id;

  const loadRecords = async () => {
    setLoading(true);
    try {
      let url;
      if (selectedScenario === '_legacy') {
        url = `/api/learning/records?user_id=${user_id}`;
      } else if (selectedScenario) {
        url = `/api/snap/records?user_id=${user_id}&scenario_type=${selectedScenario}`;
      } else {
        url = `/api/snap/records?user_id=${user_id}`;
      }

      if (searchKeyword) {
        url += `${url.includes('?') ? '&' : '?'}keyword=${encodeURIComponent(searchKeyword)}`;
      }

      const response = await fetch(url, { credentials: 'include' });
      const result = await response.json();

      if (result.success) {
        let data = result.data;
        if (selectedScenario === '_legacy') {
          data = data.filter(r => !r.scenario_type);
        }
        setRecords(data);
      }
    } catch (error) {
      console.error('Load records error:', error);
      toast.error('기록 불러오기에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
  }, [selectedScenario, searchKeyword]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchKeyword(searchInput.trim());
  };

  const handleReview = async (record) => {
    try {
      const apiUrl = record.scenario_type ? '/api/snap/records' : '/api/learning/records';
      const response = await fetch(apiUrl, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          record_id: record.record_id,
          review_count: (record.review_count || 0) + 1,
        }),
      });
      const result = await response.json();
      if (result.success) {
        loadRecords();
        toast.success('복습 완료!');
      }
    } catch (error) {
      toast.error('복습 체크 실패');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const apiUrl = deleteTarget.scenario_type ? '/api/snap/records' : '/api/learning/records';
      const response = await fetch(`${apiUrl}?record_id=${deleteTarget.record_id}`, { method: 'DELETE', credentials: 'include' });
      const result = await response.json();
      if (result.success) {
        loadRecords();
        toast.success('삭제되었습니다.');
      } else {
        toast.error('삭제 실패: ' + (result.error || ''));
      }
    } catch (error) {
      toast.error('삭제 실패');
    } finally {
      setDeleteTarget(null);
    }
  };

  const getConditionColor = (analysis) => {
    if (!analysis) return null;
    try {
      const a = typeof analysis === 'string' ? JSON.parse(analysis) : analysis;
      return { '양호': '#28a745', '주의': '#ffc107', '불량': '#fd7e14', '위험': '#dc3545' }[a.condition] || null;
    } catch { return null; }
  };

  return (
    <div className="page-with-nav" style={{
      minHeight: '100vh',
      background: 'var(--gradient-bg)',
      padding: 'var(--space-lg)',
    }}>
      <div style={{ maxWidth: 'var(--max-width-wide)', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: 'var(--space-lg)',
          animation: 'fadeInDown 0.4s ease forwards',
        }}>
          <button onClick={() => navigate('/')}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: 'white',
            }}>
            ←
          </button>
          <h1 style={{
            fontSize: 'var(--font-size-2xl)',
            color: 'white',
            margin: 0,
          }}>
            📋 내 기록
          </h1>
        </div>

        {/* Search bar */}
        <form
          onSubmit={handleSearch}
          style={{
            display: 'flex',
            gap: 'var(--space-sm)',
            marginBottom: 'var(--space-md)',
            animation: 'fadeInDown 0.4s ease 0.1s forwards',
            opacity: 0,
          }}
        >
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="키워드 검색 (문제, 과목, 설명 등)"
            style={{
              flex: 1,
              padding: '0.6rem 1rem',
              border: 'none',
              borderRadius: 'var(--radius-full)',
              background: 'rgba(255,255,255,0.12)',
              color: 'white',
              fontSize: 'var(--font-size-sm)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }}
          />
          <button
            type="submit"
            style={{
              padding: '0.6rem 1.2rem',
              background: 'var(--color-accent)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-full)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            🔍
          </button>
          {searchKeyword && (
            <button
              type="button"
              onClick={() => { setSearchInput(''); setSearchKeyword(''); }}
              style={{
                padding: '0.6rem 1rem',
                background: 'rgba(255,255,255,0.1)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-full)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                flexShrink: 0,
              }}
            >
              초기화
            </button>
          )}
        </form>

        {/* Filter tabs - horizontal scroll */}
        <div
          className="hide-scrollbar"
          style={{
            display: 'flex',
            gap: 'var(--space-sm)',
            marginBottom: 'var(--space-lg)',
            overflowX: 'auto',
            paddingBottom: '4px',
          }}
        >
          {SCENARIO_TABS.map(tab => {
            const isActive = selectedScenario === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setSelectedScenario(tab.key)}
                style={{
                  padding: '0.5rem 1rem',
                  border: 'none',
                  borderRadius: 'var(--radius-full)',
                  background: isActive
                    ? 'rgba(255,255,255,0.25)'
                    : 'rgba(255,255,255,0.08)',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: isActive ? 700 : 400,
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  whiteSpace: 'nowrap',
                  transition: 'all var(--transition-fast)',
                  boxShadow: isActive ? '0 0 0 2px rgba(255,255,255,0.4)' : 'none',
                  flexShrink: 0,
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Loading skeleton */}
        {loading && <SkeletonGrid count={6} />}

        {/* Empty state */}
        {!loading && records.length === 0 && (
          <div style={{
            background: 'white',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--space-2xl) var(--space-lg)',
            textAlign: 'center',
            animation: 'fadeInUp 0.4s ease forwards',
          }}>
            <div style={{ fontSize: '4rem', marginBottom: 'var(--space-md)', opacity: 0.4 }}>📭</div>
            <p style={{
              color: 'var(--color-text-secondary)',
              fontSize: 'var(--font-size-lg)',
              fontWeight: 500,
              marginBottom: 'var(--space-sm)',
            }}>
              아직 기록이 없습니다
            </p>
            <p style={{
              color: 'var(--color-text-muted)',
              fontSize: 'var(--font-size-sm)',
              marginBottom: 'var(--space-lg)',
            }}>
              사진을 촬영하고 AI 분석을 시작해보세요
            </p>
            <button onClick={() => navigate('/')}
              style={{
                padding: '0.75rem 2rem',
                background: 'var(--color-accent)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-full)',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: 'var(--font-size-base)',
              }}>
              시나리오 선택하기
            </button>
          </div>
        )}

        {/* Record grid */}
        {!loading && records.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 'var(--space-md)',
          }}>
            {records.map((record, index) => {
              const condColor = getConditionColor(record.analysis_json);
              return (
                <div
                  key={record.record_id}
                  style={{
                    background: 'white',
                    borderRadius: 'var(--radius-xl)',
                    padding: '1.25rem',
                    boxShadow: 'var(--shadow-md)',
                    cursor: 'pointer',
                    transition: 'transform var(--transition-fast), box-shadow var(--transition-fast)',
                    position: 'relative',
                    overflow: 'hidden',
                    opacity: 0,
                    animation: 'fadeInUp 0.4s ease forwards',
                    animationDelay: `${index * 50}ms`,
                  }}
                  onClick={() => navigate(`/records/${record.record_id}`)}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = '';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  {/* Left accent bar */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    width: '4px',
                    background: condColor || (record.mastered ? 'var(--color-success)' : 'transparent'),
                  }} />

                  {/* Tags */}
                  <div style={{
                    display: 'flex',
                    gap: '0.3rem',
                    flexWrap: 'wrap',
                    marginBottom: '0.75rem',
                  }}>
                    {record.scenario_type && (
                      <span style={{
                        padding: '0.2rem 0.6rem',
                        background: SCENARIOS[record.scenario_type]?.color || 'var(--color-accent)',
                        color: 'white',
                        borderRadius: 'var(--radius-full)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 600,
                      }}>
                        {SCENARIOS[record.scenario_type]
                          ? `${SCENARIOS[record.scenario_type].icon} ${SCENARIOS[record.scenario_type].name}`
                          : record.scenario_type}
                      </span>
                    )}
                    {record.level && (
                      <span style={{
                        padding: '0.2rem 0.6rem',
                        background: '#764ba2',
                        color: 'white',
                        borderRadius: 'var(--radius-full)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 600,
                      }}>
                        {record.level}
                      </span>
                    )}
                    {record.subject && (
                      <span style={{
                        padding: '0.2rem 0.6rem',
                        background: '#e67e22',
                        color: 'white',
                        borderRadius: 'var(--radius-full)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 600,
                      }}>
                        {record.subject}
                      </span>
                    )}
                  </div>

                  {/* Image */}
                  {record.image_url && (
                    <div style={{
                      marginBottom: '0.75rem',
                      borderRadius: 'var(--radius-md)',
                      overflow: 'hidden',
                    }}>
                      <img src={record.image_url} alt="이미지"
                        style={{
                          width: '100%',
                          height: '120px',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    </div>
                  )}

                  {/* Content preview */}
                  <div style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-text-secondary)',
                    marginBottom: '0.75rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: 1.5,
                  }}>
                    {record.problem_text || record.explanation || '(내용 없음)'}
                  </div>

                  {/* Meta */}
                  <div style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-muted)',
                    marginBottom: '0.75rem',
                  }}>
                    {record.difficulty && <span>난이도: {record.difficulty} | </span>}
                    복습: {record.review_count || 0}회 |
                    {' '}{new Date(record.created_at).toLocaleDateString('ko-KR')}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleReview(record); }}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        background: 'var(--color-accent)',
                        color: 'white',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        cursor: 'pointer',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 600,
                        transition: 'opacity var(--transition-fast)',
                      }}>
                      복습
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(record); }}
                      style={{
                        padding: '0.5rem 0.7rem',
                        background: 'var(--color-danger)',
                        color: 'white',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        cursor: 'pointer',
                        fontSize: 'var(--font-size-sm)',
                        transition: 'opacity var(--transition-fast)',
                      }}>
                      🗑
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete confirm modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        title="기록 삭제"
        message="이 기록을 정말 삭제하시겠습니까? 삭제된 기록은 복구할 수 없습니다."
        confirmText="삭제"
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
